import { useState } from 'react';

import { buildCommands } from '../core/commands';
import { LIMITS, RECOMMENDED_CARRIER_HZ, STEPS, dutyCycle } from '../core/limits';
import type { CycleSettings } from '../core/types';
import { formatDuration, validateProgram, type Issue } from '../core/validator';
import { useConnection } from '../store/connection';
import { BLANK_CYCLE, useStore } from '../store/store';
import { Modal, Segmented, Slider } from './common';
import { useConfirm } from './confirm';

/**
 * Program editor — a chain of steps.
 *
 * Each step holds one frequency (or one sweep), how long it runs, how strong it
 * is, and how long to rest before the next. The device takes up to seven.
 *
 * Reordering uses up/down buttons rather than drag: dragging inside a scrolling
 * list is unreliable on touch and impossible with a keyboard.
 */
export function Editor() {
  const store = useStore();
  const conn = useConnection();
  const confirm = useConfirm();
  const program = store.current;
  const [open, setOpen] = useState(0);
  const [showCommands, setShowCommands] = useState(false);
  const [sending, setSending] = useState(false);

  if (!program) {
    return (
      <div className="empty">
        <p>No program open.</p>
        <button className="btn primary" onClick={() => store.createProgram()}>
          Create one
        </button>
      </div>
    );
  }

  const result = validateProgram(program, store.profile.avoidBands);
  const connected = conn.state === 'connected';
  const atMax = program.cycles.length >= LIMITS.cycles.max;

  const patch = (i: number, p: Partial<CycleSettings>) => store.updateCycle(program.id, i, p);

  const addStep = () => {
    if (atMax) return;
    const last = program.cycles[program.cycles.length - 1];
    store.setCycles(program.id, [...program.cycles, { ...last, offMin: 0 }]);
    setOpen(program.cycles.length);
  };

  const removeStep = (i: number) => {
    if (program.cycles.length <= 1) return;
    store.setCycles(
      program.id,
      program.cycles.filter((_, n) => n !== i),
    );
    setOpen((cur) => Math.max(0, Math.min(cur, program.cycles.length - 2)));
  };

  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= program.cycles.length) return;
    const next = [...program.cycles];
    [next[i], next[j]] = [next[j], next[i]];
    store.setCycles(program.id, next);
    setOpen(j);
  };

  const run = async () => {
    if (!connected || !result.canRun) return;
    setSending(true);
    try {
      await conn.sendProgram(program, { run: true });
      store.logSession(program, result.totalMin);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="row">
          <input
            type="text"
            value={program.title ?? program.name}
            onChange={(e) => store.updateProgram(program.id, { title: e.target.value })}
            aria-label="Program name"
            style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 17 }}
          />
          <button
            className={`star-btn ${program.favorite ? 'on' : ''}`}
            aria-label={program.favorite ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={!!program.favorite}
            onClick={() => store.toggleFavorite(program.id)}
          >
            {program.favorite ? '♥' : '♡'}
          </button>
        </div>
        <div className="row wrap muted" style={{ marginTop: 10 }}>
          <span>
            <strong className="mono">{formatDuration(result.totalMin)}</strong> start to finish
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <strong className="mono">{formatDuration(result.treatMin)}</strong> running
          </span>
        </div>
      </div>

      {program.info?.purpose ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            {program.info.purpose}
          </p>
          {program.info.note ? (
            <div className="issue warning" style={{ marginTop: 10, marginBottom: 0 }}>
              {program.info.note}
            </div>
          ) : null}
        </div>
      ) : null}

      <IssueList issues={result.issues} />

      <div className="section-label">Steps</div>

      {program.cycles.map((cycle, i) => (
        <div key={i}>
          <div className={`step ${open === i ? 'active' : ''}`}>
            <div
              className="step-head"
              role="button"
              tabIndex={0}
              aria-expanded={open === i}
              onClick={() => setOpen(open === i ? -1 : i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(open === i ? -1 : i);
                }
              }}
            >
              <span className="step-num">{i + 1}</span>
              <span className="step-summary grow">
                {cycle.sweepEnabled
                  ? `${cycle.sweepLo}–${cycle.sweepHi} Hz`
                  : `${cycle.gateHz} Hz`}
                <span className="faint" style={{ fontWeight: 400 }}>
                  {'  '}
                  {cycle.onMin} min · {cycle.intensity}%
                </span>
              </span>
              <span className="faint" aria-hidden="true">
                {open === i ? '▴' : '▾'}
              </span>
            </div>

            {open === i ? (
              <div className="step-body">
                {i > 0 ? (
                  <Slider
                    label="Rest before this step"
                    value={program.cycles[i - 1].offMin}
                    min={0}
                    max={480}
                    step={5}
                    unit="min"
                    onChange={(v) => patch(i - 1, { offMin: v })}
                    hint="Time with the coil off, so the body can settle between rounds."
                  />
                ) : null}

                <div className="field">
                  <Segmented
                    value={cycle.sweepEnabled ? 'sweep' : 'hold'}
                    onChange={(v) => patch(i, { sweepEnabled: v === 'sweep' })}
                    options={[
                      { value: 'hold', label: 'Hold one frequency' },
                      { value: 'sweep', label: 'Sweep a range' },
                    ]}
                  />
                </div>

                {cycle.sweepEnabled ? (
                  <>
                    <Slider
                      label="From"
                      value={cycle.sweepLo}
                      min={LIMITS.gateHz.min}
                      max={LIMITS.gateHz.max}
                      step={STEPS.gateHz}
                      unit="Hz"
                      onChange={(v) =>
                        patch(i, { sweepLo: v, sweepHi: Math.max(v + 0.5, cycle.sweepHi) })
                      }
                    />
                    <Slider
                      label="To"
                      value={cycle.sweepHi}
                      min={LIMITS.gateHz.min}
                      max={LIMITS.gateHz.max}
                      step={STEPS.gateHz}
                      unit="Hz"
                      onChange={(v) => patch(i, { sweepHi: v })}
                    />
                    <Slider
                      label="Seconds at each step"
                      value={cycle.sweepHold}
                      min={LIMITS.sweepHold.min}
                      max={LIMITS.sweepHold.max}
                      step={1}
                      unit="s"
                      onChange={(v) => patch(i, { sweepHold: v })}
                      hint={`Moves 1 Hz at a time — about ${Math.round(
                        (cycle.sweepHi - cycle.sweepLo) * cycle.sweepHold,
                      )}s for one pass.`}
                    />
                    <div className="field">
                      <Segmented
                        value={cycle.sweepTrue ? 'bounce' : 'once'}
                        onChange={(v) => patch(i, { sweepTrue: v === 'bounce' })}
                        options={[
                          { value: 'bounce', label: 'Up and back' },
                          { value: 'once', label: 'Up once' },
                        ]}
                      />
                    </div>
                  </>
                ) : (
                  <Slider
                    label="Frequency"
                    value={cycle.gateHz}
                    min={LIMITS.gateHz.min}
                    max={LIMITS.gateHz.max}
                    step={STEPS.gateHz}
                    unit="Hz"
                    onChange={(v) => patch(i, { gateHz: v })}
                  />
                )}

                <Slider
                  label="Run for"
                  value={cycle.onMin}
                  min={LIMITS.onMin.min}
                  max={LIMITS.onMin.max}
                  step={1}
                  unit="min"
                  onChange={(v) => patch(i, { onMin: v })}
                  hint={
                    cycle.onMin >= 60
                      ? `${(cycle.onMin / 60).toFixed(1)} hours. The device allows up to 8.`
                      : undefined
                  }
                />

                <Slider
                  label="Intensity"
                  value={cycle.intensity}
                  min={0}
                  max={100}
                  step={5}
                  unit="%"
                  onChange={(v) => patch(i, { intensity: v })}
                  hint={
                    cycle.intensity === 100
                      ? `Full pulse — on ${
                          dutyCycle(cycle.intensity, cycle.gateDutyPct).average
                        }% of the time, set by pulse/rest below.`
                      : `Chopped to ${cycle.intensity}% within each pulse · ${
                          dutyCycle(cycle.intensity, cycle.gateDutyPct).average
                        }% of the time overall`
                  }
                />

                <Slider
                  label="Pulse / rest"
                  value={cycle.gateDutyPct}
                  min={LIMITS.gateDutyPct.min}
                  max={LIMITS.gateDutyPct.max}
                  step={1}
                  unit="%"
                  onChange={(v) => patch(i, { gateDutyPct: v })}
                  hint={pulseShape(cycle)}
                />

                <Slider
                  label="Carrier"
                  value={cycle.carrierHz}
                  min={LIMITS.carrierHz.min}
                  max={2000}
                  step={50}
                  unit="Hz"
                  onChange={(v) => patch(i, { carrierHz: v })}
                  hint={
                    cycle.carrierHz > 1000
                      ? 'Above about 1 kHz the coil stops keeping up and the extra becomes heat.'
                      : `${RECOMMENDED_CARRIER_HZ} Hz works well. The device always runs a carrier — ${LIMITS.carrierHz.min} Hz is the lowest it allows.`
                  }
                />

                <div className="row wrap" style={{ marginBottom: 12 }}>
                  <button
                    className="step-move"
                    aria-label="Move step up"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="step-move"
                    aria-label="Move step down"
                    disabled={i === program.cycles.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </button>
                  <span className="grow" />
                  <button
                    className="btn small danger"
                    disabled={program.cycles.length <= 1}
                    onClick={() => removeStep(i)}
                  >
                    Remove step
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {i < program.cycles.length - 1 && cycle.offMin > 0 ? (
            <div className="rest-row">
              <span aria-hidden="true">↓</span>
              <span>{cycle.offMin} min rest</span>
            </div>
          ) : null}
        </div>
      ))}

      <button
        className="btn"
        style={{ width: '100%', marginBottom: 12 }}
        onClick={addStep}
        disabled={atMax}
      >
        {atMax ? `${LIMITS.cycles.max} steps is the device maximum` : '+ Add a step'}
      </button>

      <div className="card">
        <div className="row wrap">
          <button
            className="btn primary grow"
            disabled={!connected || !result.canRun || sending}
            onClick={() => void run()}
          >
            {sending ? 'Sending…' : '▶  Send and start'}
          </button>
          <button className="btn small" onClick={() => setShowCommands(true)}>
            Commands
          </button>
        </div>
        {!connected ? (
          <div className="faint" style={{ marginTop: 8 }}>
            Connect on the Home tab to run this. You can keep editing either way.
          </div>
        ) : null}
      </div>

      <div className="card">
        <div className="row wrap">
          <button
            className="btn small"
            onClick={() => {
              store.duplicateProgram(program.id);
            }}
          >
            Duplicate
          </button>
          <button
            className="btn small danger"
            onClick={async () => {
              const ok = await confirm({
                title: `Delete ${program.title ?? program.name}?`,
                body: 'This removes it from your library.',
                confirmLabel: 'Delete',
                danger: true,
              });
              if (ok) store.deleteProgram(program.id);
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {showCommands ? (
        <Modal title="What gets sent" onClose={() => setShowCommands(false)}>
          {/*
            RUN is withheld when the program fails validation.

            "Send and start" is disabled in that case, but this modal used to
            hand over the same sequence WITH RUN on the end plus a Copy button —
            and pasting into a terminal app is the normal way to drive the device
            from an iPhone. That made it a complete way around the avoid-band
            check for the profile it exists to protect.
          */}
          {!result.canRun ? (
            <div className="issue error" style={{ marginBottom: 10 }}>
              <strong>This program can't be run on {store.profile.name}.</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {result.errors.map((e, i) => (
                  <li key={i}>{e.message}</li>
                ))}
              </ul>
              <div style={{ marginTop: 6 }}>
                The setup lines are shown so you can check them, but the final{' '}
                <span className="mono">RUN</span> has been left off.
              </div>
            </div>
          ) : (
            <p className="muted" style={{ marginTop: 0 }}>
              One line at a time, with a short pause between each.
            </p>
          )}
          <pre className="pre">{buildCommands(program, { run: result.canRun }).join('\n')}</pre>
          <button
            className="btn small"
            style={{ marginTop: 12 }}
            onClick={() =>
              navigator.clipboard?.writeText(
                buildCommands(program, { run: result.canRun }).join('\n'),
              )
            }
          >
            Copy
          </button>
        </Modal>
      ) : null}
    </>
  );
}

/** "8 ms pulse, then 32 ms of silence" — the shape a duty setting produces. */
function pulseShape(cycle: CycleSettings): string {
  const hz = cycle.sweepEnabled ? (cycle.sweepLo + cycle.sweepHi) / 2 : cycle.gateHz;
  if (!hz) return '';
  const periodMs = 1000 / hz;
  const onMs = (periodMs * cycle.gateDutyPct) / 100;
  const offMs = periodMs - onMs;
  const fmt = (ms: number) => (ms >= 100 ? `${(ms / 1000).toFixed(2)} s` : `${ms.toFixed(1)} ms`);
  if (cycle.gateDutyPct === 100) return 'Continuous — no rest between pulses.';
  return `${fmt(onMs)} pulse, then ${fmt(offMs)} silent. Needs firmware with GATEDUTY.`;
}

function IssueList({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return null;
  return (
    <div className="card">
      {issues.map((issue, i) => (
        <div key={i} className={`issue ${issue.severity}`} style={{ marginBottom: 6 }}>
          {issue.cycleIndex !== undefined ? (
            <strong className="mono">Step {issue.cycleIndex + 1}: </strong>
          ) : null}
          {issue.message}
        </div>
      ))}
    </div>
  );
}

/** Add a blank step — exported so the voice screen can build programs too. */
export function blankStep(gateHz: number, onMin = 15): CycleSettings {
  return { ...BLANK_CYCLE, gateHz, onMin, sweepEnabled: false };
}
